import React, { FC, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { action } from 'typesafe-actions';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { transactionService } from '../../../../../channels/secure-web/services';
import { getLimitDateFormat, mappingOrderDirection } from '../../../../content-composer/src/utils';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { useOrderStatuses } from '../Parameters/hooks';
import { ConsolidatedTransactionsActionTypes, ConsolidatedTransactionsProviderProps } from './types';
import { ConsolidatedTransactionsContext } from './ConsolidatedTransactionsContext';
import {
  defaultConsolidatedTransactionsProviderState,
  initialConsolidatedTransactionListFilters,
} from './defaultState';
import { ConsolidatedTransactionsReducer } from './ConsolidatedTransactionsReducer';

const ConsolidatedTransactionsProvider: FC<ConsolidatedTransactionsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(ConsolidatedTransactionsReducer, defaultConsolidatedTransactionsProviderState);

  const customerId = useSelectedCustomerId();
  const orderStatuses = useOrderStatuses();
  const isTransferHistoryAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.FundsTransferHistory);

  const { t } = useTranslation();

  // Recent Consolidated Transaction list api call
  const shouldFetchConsolidatedTransactionList = customerId !== undefined && isTransferHistoryAllowed;
  const {
    data: consolidatedTransactionListData,
    error: consolidatedTransactionListError,
    isLoading: isLoadingConsolidatedTransactionList,
    mutate: consolidatedTransactionListMutator,
  } = useInvokeSWRService(
    transactionService.GetConsolidatedTransactions,
    {
      ...state.consolidatedTransactionListFilters,
      beneficiaryName: state.consolidatedTransactionListFilters?.beneficiaryName,
      fromTrxDate: state.consolidatedTransactionListFilters?.fromTrxDate,
      toTrxDate: state.consolidatedTransactionListFilters?.toTrxDate,
      valueDateFrom: state.consolidatedTransactionListFilters?.valueDateFrom,
      valueDateTo: state.consolidatedTransactionListFilters?.valueDateTo,
      fromAmount: state.consolidatedTransactionListFilters?.fromAmount ?? '0.00',
      toAmount: state.consolidatedTransactionListFilters?.toAmount ?? '9999999999.99',
      accountNumber: state.consolidatedTransactionListFilters?.account?.accountNumber ?? undefined,
      customerId,
      orderDirection: mappingOrderDirection(t, state.consolidatedTransactionListFilters.orderDirection),
      meta: {
        timestamp: state.consolidatedTransactionListFilters?.timestamp,
      },
    },
    shouldFetchConsolidatedTransactionList,
  );

  // Callbacks for handling Consolidated Transactions filters and selected transaction
  const onConsolidatedTransactionListFiltersChange = useCallback(
    (filters, nextPage: number = 1, totalPages: number = 1) => {
      const newFilters = [
        { key: 'beneficiaryName', value: filters.beneficiaryName },
        { key: 'fromAmount', value: filters.fromAmount },
        { key: 'toAmount', value: filters.toAmount },
        { key: 'orderBeneficiaryAccount', value: filters.orderBeneficiaryAccount },
        { key: 'orderStatus', value: filters.orderStatus },
        {
          key: 'orderStatusDescription',
          value: orderStatuses?.find(({ serialNumber }) => serialNumber === filters?.orderStatus)?.description,
        },
        { key: 'customerId', value: customerId },
        { key: 'account', value: filters.account },
        {
          key: 'fromTrxDate',
          value: filters?.fromTrxDate ? getLimitDateFormat(filters.fromTrxDate, true) : null,
        },
        {
          key: 'toTrxDate',
          value: filters?.toTrxDate ? getLimitDateFormat(new Date(filters.toTrxDate)) : null,
        },
        {
          key: 'valueDateFrom',
          value: filters?.valueDateFrom ? getLimitDateFormat(filters.valueDateFrom, true) : null,
        },
        {
          key: 'valueDateTo',
          value: filters?.valueDateTo ? getLimitDateFormat(new Date(filters.valueDateTo)) : null,
        },
        { key: 'orderDirection', value: filters.orderDirection },
      ].reduce((acc, { key, value }) => {
        if (value?.toString()) {
          acc[key] = value;
        }

        return acc;
      }, {} as GenericObject);

      if (
        typeof newFilters.account !== typeof state.consolidatedTransactionListFilters.account ||
        newFilters?.account?.accountId !== state.consolidatedTransactionListFilters.account?.accountId ||
        newFilters.pageOffset !== state.consolidatedTransactionListFilters.pageOffset
      ) {
        newFilters.timestamp = Date.now().toString();
        window.scrollTo(0, 0);
      }
      dispatch(
        action(ConsolidatedTransactionsActionTypes.setConsolidatedTransactionListFilters, {
          ...newFilters,
          pageOffset: filters.pageSize
            ? Math.min(Math.max(nextPage - 1, 0), totalPages) * Math.max(filters.pageSize ?? 1, 1)
            : 0,
          pageSize: filters.pageSize ? state.consolidatedTransactionListFilters.pageSize : 10,
        }),
      );
    },
    [customerId, state.consolidatedTransactionListFilters, orderStatuses],
  );

  const resetConsolidatedTransactionsListFilters = useCallback(() => {
    dispatch(
      action(ConsolidatedTransactionsActionTypes.setConsolidatedTransactionListFilters, {
        ...initialConsolidatedTransactionListFilters,
        timestamp: Date.now().toString(),
      }),
    );
  }, []);

  const onSetLastPaginationPageVisited = useCallback((value: number) => {
    dispatch(action(ConsolidatedTransactionsActionTypes.setLastPaginationPageVisited, value));
  }, []);

  const refetchConsolidatedTransactionList = useCallback(() => {
    consolidatedTransactionListMutator && consolidatedTransactionListMutator();
  }, [consolidatedTransactionListMutator]);

  const values = {
    ...state,
    consolidatedTransactionListData,
    resetConsolidatedTransactionsListFilters,
    onConsolidatedTransactionListFiltersChange,
    refetchConsolidatedTransactionList,
    loadingState: {
      isLoadingConsolidatedTransactionList,
    },
    errorState: {
      consolidatedTransactionListError,
    },
    onSetLastPaginationPageVisited,
  };

  return <ConsolidatedTransactionsContext.Provider value={values}>{children}</ConsolidatedTransactionsContext.Provider>;
};

export { ConsolidatedTransactionsProvider };
